import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Download,
  Upload,
  MessageCircle,
  Star,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  List,
  SlidersHorizontal,
  UserPlus,
  Tag,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { ContactCard } from "./ContactCard";
import { ContactTable } from "./ContactTable";
import { ContactDetails } from "./ContactDetails";

export type Contact = {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  company: string;
  role: string;
  location: string;
  channel: "whatsapp" | "instagram" | "facebook" | "widget";
  status: "active" | "inactive" | "lead" | "customer";
  tags: string[];
  isFavorite: boolean;
  lastContact: string;
  totalConversations: number;
  createdAt: string;
  notes?: string;
};

const initialContacts: Contact[] = [
  {
    id: "1",
    name: "Maria Santos",
    email: "maria.santos@email.com",
    phone: "+55 11 99999-1234",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
    company: "Tech Solutions Ltda",
    role: "Diretora de Marketing",
    location: "São Paulo, SP",
    channel: "whatsapp",
    status: "customer",
    tags: ["VIP", "Enterprise"],
    isFavorite: true,
    lastContact: "Há 2 horas",
    totalConversations: 47,
    createdAt: "15/01/2024",
  },
  {
    id: "2",
    name: "João Pedro Oliveira",
    email: "joao.pedro@empresa.com.br",
    phone: "+55 21 98888-5678",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    company: "Oliveira & Associados",
    role: "CEO",
    location: "Rio de Janeiro, RJ",
    channel: "whatsapp",
    status: "customer",
    tags: ["VIP", "Parceiro"],
    isFavorite: true,
    lastContact: "Há 5 horas",
    totalConversations: 32,
    createdAt: "10/01/2024",
  },
  {
    id: "3",
    name: "Ana Julia Ferreira",
    email: "ana.julia@startup.io",
    phone: "+55 31 97777-4321",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    company: "Startup Innovation",
    role: "Product Manager",
    location: "Belo Horizonte, MG",
    channel: "instagram",
    status: "lead",
    tags: ["Lead", "Startup"],
    isFavorite: false,
    lastContact: "Há 1 dia",
    totalConversations: 8,
    createdAt: "20/01/2024",
  },
  {
    id: "4",
    name: "Carlos Eduardo Silva",
    email: "carlos.eduardo@corp.com",
    phone: "+55 41 96666-8765",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face",
    company: "Corporate Brasil",
    role: "Gerente Comercial",
    location: "Curitiba, PR",
    channel: "whatsapp",
    status: "customer",
    tags: ["Premium"],
    isFavorite: false,
    lastContact: "Há 3 dias",
    totalConversations: 23,
    createdAt: "05/01/2024",
  },
  {
    id: "5",
    name: "Fernanda Lima Costa",
    email: "fernanda@agencia.digital",
    phone: "+55 51 95555-2468",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face",
    company: "Agência Digital PRO",
    role: "Account Manager",
    location: "Porto Alegre, RS",
    channel: "facebook",
    status: "active",
    tags: ["Agência", "Parceiro"],
    isFavorite: true,
    lastContact: "Há 6 horas",
    totalConversations: 56,
    createdAt: "01/12/2023",
  },
  {
    id: "6",
    name: "Ricardo Nascimento",
    email: "ricardo@ecommerce.com",
    phone: "+55 11 94444-1357",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face",
    company: "E-commerce Brasil",
    role: "Founder",
    location: "São Paulo, SP",
    channel: "widget",
    status: "customer",
    tags: ["VIP", "E-commerce"],
    isFavorite: false,
    lastContact: "Há 12 horas",
    totalConversations: 89,
    createdAt: "15/11/2023",
  },
  {
    id: "7",
    name: "Patrícia Mendes",
    email: "patricia.mendes@consultoria.com",
    phone: "+55 27 93333-9876",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face",
    company: "Consultoria Empresarial",
    role: "Consultora Senior",
    location: "Vitória, ES",
    channel: "whatsapp",
    status: "lead",
    tags: ["Lead", "Consultoria"],
    isFavorite: false,
    lastContact: "Há 2 dias",
    totalConversations: 5,
    createdAt: "25/01/2024",
  },
  {
    id: "8",
    name: "Lucas Ferreira Santos",
    email: "lucas.ferreira@tech.io",
    phone: "+55 48 92222-6543",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    company: "TechFlow Solutions",
    role: "CTO",
    location: "Florianópolis, SC",
    channel: "whatsapp",
    status: "customer",
    tags: ["Tech", "Enterprise"],
    isFavorite: true,
    lastContact: "Há 4 horas",
    totalConversations: 34,
    createdAt: "10/12/2023",
  },
  {
    id: "9",
    name: "Beatriz Almeida",
    email: "beatriz@marketing.agency",
    phone: "+55 61 91111-3210",
    avatar: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=100&h=100&fit=crop&crop=face",
    company: "Marketing Agency",
    role: "Marketing Director",
    location: "Brasília, DF",
    channel: "instagram",
    status: "active",
    tags: ["Marketing", "Agência"],
    isFavorite: false,
    lastContact: "Há 1 semana",
    totalConversations: 12,
    createdAt: "20/12/2023",
  },
  {
    id: "10",
    name: "Eduardo Souza Neto",
    email: "eduardo@logistics.com.br",
    phone: "+55 71 90000-7890",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face",
    company: "Logística Express",
    role: "Operations Manager",
    location: "Salvador, BA",
    channel: "whatsapp",
    status: "inactive",
    tags: ["Logística"],
    isFavorite: false,
    lastContact: "Há 2 semanas",
    totalConversations: 18,
    createdAt: "05/11/2023",
  },
  {
    id: "11",
    name: "Camila Rodrigues",
    email: "camila@fintech.io",
    phone: "+55 11 89999-4567",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face",
    company: "Fintech Solutions",
    role: "Head of Sales",
    location: "São Paulo, SP",
    channel: "whatsapp",
    status: "customer",
    tags: ["Fintech", "VIP"],
    isFavorite: true,
    lastContact: "Há 1 hora",
    totalConversations: 67,
    createdAt: "01/10/2023",
  },
  {
    id: "12",
    name: "Rafael Costa Lima",
    email: "rafael@industria.com.br",
    phone: "+55 19 88888-1234",
    avatar: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face",
    company: "Indústria Nacional",
    role: "Diretor Industrial",
    location: "Campinas, SP",
    channel: "widget",
    status: "lead",
    tags: ["Indústria", "Lead"],
    isFavorite: false,
    lastContact: "Há 3 dias",
    totalConversations: 3,
    createdAt: "28/01/2024",
  },
];

export function ContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>(initialContacts);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Filter contacts
  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phone.includes(searchQuery);
    
    const matchesStatus = statusFilter === "all" || contact.status === statusFilter;
    const matchesChannel = channelFilter === "all" || contact.channel === channelFilter;

    return matchesSearch && matchesStatus && matchesChannel;
  });

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleToggleFavorite = (contactId: string) => {
    setContacts((prev) =>
      prev.map((c) =>
        c.id === contactId ? { ...c, isFavorite: !c.isFavorite } : c
      )
    );
  };

  const handleDeleteContact = (contactId: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== contactId));
    if (selectedContact?.id === contactId) {
      setSelectedContact(null);
    }
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const stats = {
    total: contacts.length,
    customers: contacts.filter((c) => c.status === "customer").length,
    leads: contacts.filter((c) => c.status === "lead").length,
    active: contacts.filter((c) => c.status === "active").length,
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-4 gap-4 border-b border-[#E2E8F0] bg-white px-6 py-4"
        >
          <StatCard label="Total de Contatos" value={stats.total} icon={UserPlus} color="#2563EB" />
          <StatCard label="Clientes" value={stats.customers} icon={Star} color="#22C55E" />
          <StatCard label="Leads" value={stats.leads} icon={Tag} color="#F59E0B" />
          <StatCard label="Ativos" value={stats.active} icon={MessageCircle} color="#8B5CF6" />
        </motion.div>

        {/* Toolbar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E2E8F0] bg-white px-6 py-4"
        >
          {/* Left Side */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
              <input
                type="text"
                placeholder="Buscar contatos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 w-72 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] pl-10 pr-4 text-sm text-[#0F172A] placeholder:text-[#94A3B8] transition-all focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all",
                showFilters
                  ? "border-[#2563EB] bg-blue-50 text-[#2563EB]"
                  : "border-[#E2E8F0] bg-white text-[#64748B] hover:bg-[#F8FAFC]"
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filtros
            </button>

            {/* Quick Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="flex items-center gap-2"
                >
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm text-[#64748B] focus:border-[#2563EB] focus:outline-none"
                  >
                    <option value="all">Todos Status</option>
                    <option value="customer">Clientes</option>
                    <option value="lead">Leads</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                  </select>

                  <select
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                    className="h-10 rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm text-[#64748B] focus:border-[#2563EB] focus:outline-none"
                  >
                    <option value="all">Todos Canais</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="widget">Chat Widget</option>
                  </select>

                  <button
                    onClick={() => {
                      setStatusFilter("all");
                      setChannelFilter("all");
                    }}
                    className="text-sm text-[#64748B] hover:text-[#EF4444]"
                  >
                    Limpar
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-all",
                  viewMode === "grid"
                    ? "bg-white text-[#2563EB] shadow-sm"
                    : "text-[#64748B] hover:text-[#0F172A]"
                )}
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-all",
                  viewMode === "table"
                    ? "bg-white text-[#2563EB] shadow-sm"
                    : "text-[#64748B] hover:text-[#0F172A]"
                )}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            {/* Import/Export */}
            <button className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm font-medium text-[#64748B] transition-all hover:bg-[#F8FAFC]">
              <Upload className="h-4 w-4" />
              Importar
            </button>
            <button className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm font-medium text-[#64748B] transition-all hover:bg-[#F8FAFC]">
              <Download className="h-4 w-4" />
              Exportar
            </button>

            {/* Add Contact */}
            <button className="flex items-center gap-2 rounded-xl bg-[#2563EB] px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-[#1E40AF]">
              <Plus className="h-4 w-4" />
              Novo Contato
            </button>
          </div>
        </motion.div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {viewMode === "grid" ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
            >
              {paginatedContacts.map((contact, index) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  delay={index * 0.05}
                  onToggleFavorite={handleToggleFavorite}
                  onSelect={handleSelectContact}
                  isSelected={selectedContact?.id === contact.id}
                />
              ))}
            </motion.div>
          ) : (
            <ContactTable
              contacts={paginatedContacts}
              onToggleFavorite={handleToggleFavorite}
              onDelete={handleDeleteContact}
              onSelect={handleSelectContact}
              selectedContactId={selectedContact?.id}
            />
          )}

          {/* Empty State */}
          {filteredContacts.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F1F5F9]">
                <Search className="h-8 w-8 text-[#94A3B8]" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-[#0F172A]">
                Nenhum contato encontrado
              </h3>
              <p className="mt-2 text-sm text-[#64748B]">
                Tente ajustar os filtros ou adicione um novo contato
              </p>
            </motion.div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 flex items-center justify-between"
            >
              <p className="text-sm text-[#64748B]">
                Mostrando {(currentPage - 1) * itemsPerPage + 1} a{" "}
                {Math.min(currentPage * itemsPerPage, filteredContacts.length)} de{" "}
                {filteredContacts.length} contatos
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E8F0] bg-white text-[#64748B] transition-all hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-all",
                      page === currentPage
                        ? "bg-[#2563EB] text-white"
                        : "border border-[#E2E8F0] bg-white text-[#64748B] hover:bg-[#F8FAFC]"
                    )}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E8F0] bg-white text-[#64748B] transition-all hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Contact Details Sidebar */}
      <AnimatePresence>
        {selectedContact && (
          <ContactDetails
            contact={selectedContact}
            onClose={() => setSelectedContact(null)}
            onToggleFavorite={handleToggleFavorite}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
}

function StatCard({ label, value, icon: Icon, color }: StatCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[#F8FAFC] p-4">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#0F172A]">{value}</p>
        <p className="text-xs text-[#64748B]">{label}</p>
      </div>
    </div>
  );
}